// Singleton that fetches the admin-uploaded default Bass Kit and rebuilds a
// Tone.Sampler from it. The Sampler interpolates between any uploaded sample
// pitches (C1..C4), so a single sample at C2 still covers the full bass range
// via pitch-shifting. The GenPlayer and DAW AudioEngine consult this manager
// before falling back to the "Galaxy Synth".
import * as Tone from "tone";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

class BassKitManager {
  constructor() {
    this.kit = null;
    this.sampler = null;     // Tone.Sampler when at least one sample is loaded
    this.loaded = false;
    this.subscribers = new Set();
  }

  async refresh(force = false) {
    if (this.loaded && !force) return this.kit;
    try {
      const res = await fetch(`${BACKEND_URL}/api/training/bass-kits/default`);
      const data = await res.json();
      const kit = data?.kit || null;
      // Dispose old sampler
      if (this.sampler) { try { this.sampler.dispose(); } catch { /* noop */ } }
      this.sampler = null;
      this.kit = kit;

      const samples = kit?.samples || {};
      const urlMap = {};
      for (const [pitch, meta] of Object.entries(samples)) {
        if (meta?.url) urlMap[pitch] = `${BACKEND_URL}${meta.url}`;
      }
      if (Object.keys(urlMap).length > 0) {
        await Tone.start();
        this.sampler = new Tone.Sampler({ urls: urlMap }).toDestination();
        await Tone.loaded();
      }
      this.loaded = true;
      this.subscribers.forEach((fn) => { try { fn(this.kit); } catch { /* noop */ } });
      return this.kit;
    } catch (e) {
      console.warn("BassKit: refresh failed", e);
      return null;
    }
  }

  isReady() { return !!(this.sampler && this.sampler.loaded !== false); }

  // Absolute URL map { pitch: url } for the loaded bass kit — consumed by
  // the offline exporter to instantiate a fresh Tone.Sampler inside the
  // Tone.Offline context (live sampler is bound to online audio context).
  getSampleUrls() {
    const out = {};
    const samples = this.kit?.samples || {};
    for (const [pitch, meta] of Object.entries(samples)) {
      if (meta?.url) out[pitch] = `${BACKEND_URL}${meta.url}`;
    }
    return out;
  }

  // Fire a bass note via the sampler. Returns true if it played so the caller
  // can skip the synth fallback. duration is in seconds.
  trigger(pitch, time, durationSec = 0.25, velocity = 1) {
    if (!this.isReady()) return false;
    try {
      this.sampler.triggerAttackRelease(pitch, durationSec, time, velocity);
      return true;
    } catch {
      return false;
    }
  }

  getKit() { return this.kit; }
  subscribe(fn) { this.subscribers.add(fn); return () => this.subscribers.delete(fn); }
}

const bassKitManager = new BassKitManager();
export default bassKitManager;
